export function sharedUtils(): string {
  return 'shared-utils';
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, exponent);
  const unit = units[exponent];

  if (unit === 'B') return `${value} B`;

  const rounded = value < 10 ? value.toFixed(1).replace(/\.0$/, '') : Math.round(value).toString();
  return `${rounded}${unit}`;
}

/**
 * Base units a product can be sold in.
 *
 * Deliberately only four, and all of them the *smallest* practical unit of
 * their kind. Kilograms, litres and dozens are display conversions applied at
 * the edges — storing them would mean "1 kg" and "1000 g" are different values
 * that no longer sort, filter or compare, which is exactly the bug that makes
 * a price-per-unit feature impossible later.
 */
export type ProductUnit = 'piece' | 'g' | 'ml' | 'cm';

export const PRODUCT_UNITS: ProductUnit[] = ['piece', 'g', 'ml', 'cm'];

/** What a merchant picks in the UI, and the base unit each one converts to. */
export interface UnitChoice {
  /** Value used in the merchant-facing dropdown. */
  id: string;
  label: string;
  base: ProductUnit;
  /** How many base units one of these is worth. */
  factor: number;
}

export const UNIT_CHOICES: UnitChoice[] = [
  { id: 'piece', label: 'Piece(s)', base: 'piece', factor: 1 },
  { id: 'pack', label: 'Pack of', base: 'piece', factor: 1 },
  { id: 'dozen', label: 'Dozen', base: 'piece', factor: 12 },
  { id: 'g', label: 'Gram (g)', base: 'g', factor: 1 },
  { id: 'kg', label: 'Kilogram (kg)', base: 'g', factor: 1000 },
  { id: 'ml', label: 'Millilitre (ml)', base: 'ml', factor: 1 },
  { id: 'l', label: 'Litre (L)', base: 'ml', factor: 1000 },
  { id: 'cm', label: 'Centimetre (cm)', base: 'cm', factor: 1 },
  { id: 'm', label: 'Metre (m)', base: 'cm', factor: 100 },
];

export function unitChoice(id: string): UnitChoice | undefined {
  return UNIT_CHOICES.find(choice => choice.id === id);
}

/**
 * Merchant input -> storage. Returns null when the pair is unusable, so a
 * half-filled form stores nothing rather than "0 g".
 */
export function toBaseUnit(
  quantity: number | null | undefined,
  choiceId: string | null | undefined
): { unitQuantity: number; unitOfMeasure: ProductUnit } | null {
  const choice = choiceId ? unitChoice(choiceId) : undefined;
  if (!choice || quantity == null || !Number.isFinite(quantity) || quantity <= 0) return null;

  // Rounded, because the column is an integer: 0.5 kg is 500 g exactly, and
  // 1.0005 kg is a typo the merchant should not be able to persist as 1000.5.
  const unitQuantity = Math.round(quantity * choice.factor);
  return unitQuantity > 0 ? { unitQuantity, unitOfMeasure: choice.base } : null;
}

/**
 * Storage -> display. Promotes to the larger unit once the amount is big
 * enough, the way a label on a shelf would read: 1500 ml shows as "1.5 L",
 * never "1500 ml".
 */
export function formatUnit(
  unitQuantity: number | null | undefined,
  unitOfMeasure: string | null | undefined
): string {
  if (unitQuantity == null || !unitOfMeasure || unitQuantity <= 0) return '';

  switch (unitOfMeasure) {
    case 'piece':
      return unitQuantity === 1 ? '1 piece' : `${unitQuantity} pieces`;
    case 'g':
      return unitQuantity >= 1000 ? `${trimNumber(unitQuantity / 1000)} kg` : `${trimNumber(unitQuantity)} g`;
    case 'ml':
      return unitQuantity >= 1000 ? `${trimNumber(unitQuantity / 1000)} L` : `${trimNumber(unitQuantity)} ml`;
    case 'cm':
      return unitQuantity >= 100 ? `${trimNumber(unitQuantity / 100)} m` : `${trimNumber(unitQuantity)} cm`;
    default:
      return `${trimNumber(unitQuantity)} ${unitOfMeasure}`;
  }
}

/**
 * The display unit a stored amount would be shown in — used to preselect the
 * dropdown when a merchant reopens a product, so "1 kg" does not come back as
 * "1000 g" and quietly retrain them to type grams.
 */
export function displayUnitChoice(
  unitQuantity: number | null | undefined,
  unitOfMeasure: string | null | undefined
): { quantity: number; choiceId: string } | null {
  if (unitQuantity == null || !unitOfMeasure || unitQuantity <= 0) return null;

  if (unitOfMeasure === 'g' && unitQuantity >= 1000) return { quantity: unitQuantity / 1000, choiceId: 'kg' };
  if (unitOfMeasure === 'ml' && unitQuantity >= 1000) return { quantity: unitQuantity / 1000, choiceId: 'l' };
  if (unitOfMeasure === 'cm' && unitQuantity >= 100) return { quantity: unitQuantity / 100, choiceId: 'm' };
  return { quantity: unitQuantity, choiceId: unitOfMeasure };
}

/** 1.5 -> "1.5", 2.0 -> "2" — no trailing zeros on a shelf label. */
function trimNumber(value: number): string {
  return Number(value.toFixed(3)).toString();
}

/**
 * The label a shopper reads for one variant.
 *
 * Variants come in two flavours and a catalogue usually has both:
 *
 *   - **measured** — 500 ml, 1 kg, a pack of 6. The size *is* the difference,
 *     and it is stored numerically so it can sort and compare.
 *   - **optioned** — S/M/L, Quarter/Half/Full, 8GB + 512GB. There is no
 *     number to compare; the difference is a named choice, or several.
 *
 * Options win when present because they are the more specific statement: a
 * phone variant carrying `{RAM: 8GB, Storage: 512GB}` and a weight of 190 g
 * should read "8GB + 512GB", not "190 g". The stored title is the last
 * resort, so a variant created before either concept still renders.
 */
export function variantLabel(variant: {
  options?: Record<string, string> | null;
  unitQuantity?: number | null;
  unitOfMeasure?: string | null;
  title?: string | null;
}): string {
  const options = variant.options ? Object.values(variant.options).filter(Boolean) : [];
  if (options.length) return options.join(' + ');

  const measured = formatUnit(variant.unitQuantity, variant.unitOfMeasure);
  if (measured) return measured;

  return variant.title?.trim() ?? '';
}

/**
 * The same variant described in full — "Size: L", "RAM: 8GB · Storage: 512GB".
 *
 * Used where there is room for the option NAMES, which the short label drops:
 * "8GB + 512GB" is fine on a chip, but an admin reviewing a catalogue needs to
 * know which number is memory and which is storage.
 */
export function variantOptionDetail(variant: {
  options?: Record<string, string> | null;
}): string {
  if (!variant.options) return '';
  return Object.entries(variant.options)
    .map(([name, value]) => `${name}: ${value}`)
    .join(' · ');
}
